import { useEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import { ReCaptchaProvider, ReCaptchaV3 } from 'react-recaptcha-x';

import { config } from 'config/env';
import useSubscriptionStatus from 'app/hooks/useSubscriptionStatus';
import FloatToCurrency from 'app/helpers/FloatToCurrency';
import { useRecaptchaTokenRefresh, useRecaptchaV3 } from 'app/hooks/useRecaptcha';

import { trackCheckoutModalViewed } from '../trackPlanAndPaymentEvents';
import { calculateCheckoutSummary } from './calculateCheckoutSummary';
import { useCheckoutPaymentMethod } from './useCheckoutPaymentMethod';
import { useCheckoutSubmission } from './useCheckoutSubmission';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import type { CheckoutModalProps } from './types';
import * as S from './styles';

const TITLE = 'Checkout';

const formatCurrency = (value: number) => `R$ ${FloatToCurrency(value)}`;

export function CheckoutModal({
  isOpen,
  onClose,
  onBack,
  productSelected,
  trackingProperties,
  jusgptLimitFunnel,
  onCheckoutSuccess,
}: CheckoutModalProps) {
  const {
    plan: currentPlanName,
    planAmount,
    startDate,
    expirationDate,
    isLoading: isCurrentPlanLoading,
  } = useSubscriptionStatus();
  const {
    mode: paymentMethodMode,
    hasSavedCard,
    savedCards,
    selectedSavedCardId,
    rawSavedCard,
    selectSavedCard,
    selectNewCard,
    formik: paymentMethodFormik,
    isPaymentValid,
  } = useCheckoutPaymentMethod();

  const refreshRecaptchaV3Ref = useRef<(() => void) | undefined>();
  const [handleRecaptchaV3Callback, captchaV3Params] = useRecaptchaV3(refreshRecaptchaV3Ref);
  useRecaptchaTokenRefresh(refreshRecaptchaV3Ref);

  const { screen, isTrialSuccess, isPendingConfirmation, submit, retry } = useCheckoutSubmission({
    productSelected,
    trackingProperties,
    paymentMethodMode,
    rawSavedCard,
    newCardValues: paymentMethodFormik.values,
    recaptchaToken: captchaV3Params.v3Token,
    jusgptLimitFunnel,
    onCheckoutSuccess,
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    trackCheckoutModalViewed();
  }, [isOpen]);

  const hasCurrentPlanData =
    !isCurrentPlanLoading && planAmount != null && Boolean(startDate) && Boolean(expirationDate);

  const checkoutSummary = hasCurrentPlanData
    ? calculateCheckoutSummary({
        currentPlanAmount: planAmount,
        currentPlanStartDate: startDate,
        currentPlanExpirationDate: expirationDate,
        newPlanAmount: productSelected.price,
      })
    : null;

  const isProcessing = screen === 'processing';
  const hasValidRecaptchaToken =
    Boolean(captchaV3Params.v3Token) && !captchaV3Params.v3Retrieving;
  const canSubmit = isPaymentValid && Boolean(checkoutSummary) && hasValidRecaptchaToken;

  const handleDialogClose = () => {
    if (isProcessing) {
      return;
    }
    onClose();
  };

  const handleRetry = () => {
    refreshRecaptchaV3Ref.current?.();
    retry();
  };

  return (
    <S.StyledDialog open={isOpen} onClose={handleDialogClose}>
      <ReCaptchaProvider siteKeyV3={config.recaptcha.siteKeyV3}>
        <ReCaptchaV3 callback={handleRecaptchaV3Callback} />
      </ReCaptchaProvider>

      <S.Head>
        <div>
          <S.Title>{TITLE}</S.Title>
          <S.Description>{productSelected.name} · assinatura mensal</S.Description>
        </div>
        {screen === 'form' || screen === 'success' ? (
          <S.CloseButton onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </S.CloseButton>
        ) : null}
      </S.Head>

      {screen === 'processing' ? (
        <S.Body>
          <S.ResultState>
            <CircularProgress size={40} />
            <S.ResultTitle>Processando sua assinatura...</S.ResultTitle>
            <S.ResultMessage>
              Não feche esta janela enquanto confirmamos o pagamento.
            </S.ResultMessage>
          </S.ResultState>
        </S.Body>
      ) : screen === 'success' ? (
        <S.Body>
          <S.ResultState>
            <S.ResultIcon $variant="success">
              <CheckCircleOutlineIcon />
            </S.ResultIcon>
            <S.ResultTitle>
              {isTrialSuccess
                ? 'Sua assinatura iniciou o período de teste!'
                : 'Parabéns, sua assinatura foi realizada com sucesso!'}
            </S.ResultTitle>
          </S.ResultState>
        </S.Body>
      ) : screen === 'error' ? (
        <S.Body>
          <S.ResultState>
            <S.ResultIcon $variant="error">
              <ErrorOutlineIcon />
            </S.ResultIcon>
            <S.ResultTitle>Não foi possível concluir sua assinatura</S.ResultTitle>
            <S.ResultMessage>
              {isPendingConfirmation
                ? 'Ainda estamos confirmando seu pagamento. Tente novamente em instantes ou feche e volte mais tarde.'
                : 'Verifique os dados do cartão e tente novamente, ou feche e volte mais tarde.'}
            </S.ResultMessage>
          </S.ResultState>
        </S.Body>
      ) : (
        <S.Body>
          <PaymentMethodSelector
            mode={paymentMethodMode}
            hasSavedCard={hasSavedCard}
            savedCards={savedCards}
            selectedSavedCardId={selectedSavedCardId}
            onSelectSavedCard={selectSavedCard}
            onSelectNewCard={selectNewCard}
            formik={paymentMethodFormik}
          />

          {checkoutSummary ? (
            <>
              <S.WarningBanner>
                <WarningAmberOutlinedIcon />
                <span>
                  Será realizado um estorno no valor de{' '}
                  <strong>{formatCurrency(checkoutSummary.refundAmount)}</strong> e uma nova cobrança
                  será efetuada com o valor do novo plano e forma de pagamento escolhidos.
                </span>
              </S.WarningBanner>

              <S.SummaryList>
                <S.SummaryRow>
                  <span>Estorno do plano {currentPlanName}</span>
                  <span>{formatCurrency(checkoutSummary.refundAmount)}</span>
                </S.SummaryRow>
                <S.SummaryRow>
                  <span>Nova cobrança · {productSelected.name}</span>
                  <span>{formatCurrency(checkoutSummary.newChargeAmount)}</span>
                </S.SummaryRow>
              </S.SummaryList>
            </>
          ) : (
            <S.ResultState>
              <CircularProgress size={32} />
              <S.ResultMessage>Calculando os valores do seu plano...</S.ResultMessage>
            </S.ResultState>
          )}
        </S.Body>
      )}

      <S.Foot $sideBySide>
        {screen === 'success' ? (
          <S.PrimaryButton type="button" onClick={onClose} style={{ marginLeft: 'auto' }}>
            Continuar navegando
          </S.PrimaryButton>
        ) : screen === 'error' ? (
          <>
            <S.SecondaryButton onClick={onClose}>Fechar</S.SecondaryButton>
            <S.PrimaryButton type="button" onClick={handleRetry}>
              Tentar novamente
            </S.PrimaryButton>
          </>
        ) : screen === 'processing' ? null : (
          <>
            <S.SecondaryButton onClick={onBack}>Voltar aos planos</S.SecondaryButton>
            <S.PrimaryButton type="button" disabled={!canSubmit} onClick={submit}>
              Assinar agora
            </S.PrimaryButton>
          </>
        )}
      </S.Foot>
    </S.StyledDialog>
  );
}

export default CheckoutModal;
